// src/admin/pages/CategoryListPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaPlus, FaEdit, FaTrashAlt, FaSearch, 
  FaChevronRight, FaChevronDown, FaExclamationTriangle 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as FaIcons from 'react-icons/fa';

interface Category {
  id: number;
  name: string;
  icon?: string;
  parentId?: number | null;
  subCategories?: Category[];
}

const CategoryListPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // States
  const [message, setMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const getFaIcon = (iconName: string, size: number = 16) => {
    const IconComponent = (FaIcons as any)[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const fetchCategories = async () => {
    try {
      // NOTE: Using localhost based on your previous files, adjust URL if needed
      const response = await fetch('https://ayushvatika.com/api/categories', {
      // const response = await fetch('http://localhost:8484/api/categories', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data: Category[] = await response.json();
        setCategories(data);

        const categoriesMap = new Map<number, Category>();
        data.forEach(cat => {
          categoriesMap.set(cat.id, { ...cat, subCategories: [] });
        });

        const rootCategories: Category[] = [];
        data.forEach(cat => {
          if (cat.parentId === null || cat.parentId === undefined) {
            rootCategories.push(categoriesMap.get(cat.id)!);
          } else {
            const parent = categoriesMap.get(cat.parentId);
            if (parent) {
              parent.subCategories?.push(categoriesMap.get(cat.id)!);
            } else {
              rootCategories.push(categoriesMap.get(cat.id)!);
            }
          }
        });
        
        rootCategories.sort((a, b) => a.name.localeCompare(b.name));
        rootCategories.forEach(cat => {
          if (cat.subCategories) {
            cat.subCategories.sort((a, b) => a.name.localeCompare(b.name));
          }
        });

        setHierarchicalCategories(rootCategories);
      } else {
        console.error("Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories: ", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) {
      return hierarchicalCategories;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    const flatFiltered = categories.filter(category =>
      category.name.toLowerCase().includes(lowerCaseSearchTerm)
    );

    const filteredCategoriesMap = new Map<number, Category>();
    flatFiltered.forEach(cat => {
      filteredCategoriesMap.set(cat.id, { ...cat, subCategories: [] });
    });

    const rootFiltered: Category[] = [];
    flatFiltered.forEach(cat => {
      if (cat.parentId === null || cat.parentId === undefined) {
        rootFiltered.push(filteredCategoriesMap.get(cat.id)!);
      } else {
        const parent = filteredCategoriesMap.get(cat.parentId);
        if (parent) {
          parent.subCategories?.push(filteredCategoriesMap.get(cat.id)!);
        }
      }
    });
    return rootFiltered;
  }, [categories, searchTerm, hierarchicalCategories]);


  const handleAddCategory = () => navigate('/admin/category/add');
  const handleEditCategory = (categoryId: number) => navigate(`/admin/category/edit/${categoryId}`);

  const handleDeleteClick = (category: Category) => {
    if (category.subCategories && category.subCategories.length > 0) {
      setWarningMessage(`Action Denied: The category "${category.name}" contains ${category.subCategories.length} sub-categories.\n\nPlease delete the sub-categories first.`);
      setShowWarningModal(true);
      return; 
    }
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      try {
        const response = await fetch(`https://ayushvatika.com/api/categories/${categoryToDelete.id}`, {
        // const response = await fetch(`http://localhost:8484/api/categories/${categoryToDelete.id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          setMessage(`Category "${categoryToDelete.name}" deleted successfully!`);
          fetchCategories();
          setShowDeleteModal(false);
          setTimeout(() => setMessage(null), 3000);
        } else {
          setShowDeleteModal(false); 
          setWarningMessage('Deletion Failed: Please check if this category contains products.\n\nGo to the Product List and delete the associated products first.');
          setShowWarningModal(true);
        }
      } catch (error) {
        setShowDeleteModal(false);
        setWarningMessage('Deletion Failed: Server error or network issue.');
        setShowWarningModal(true);
      } finally {
        setCategoryToDelete(null);
      }
    }
  };

  const cancelDelete = () => {
    setCategoryToDelete(null);
    setShowDeleteModal(false);
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) newSet.delete(categoryId);
      else newSet.add(categoryId);
      return newSet;
    });
  };

  const renderCategoryRows = (cats: Category[], level: number = 0) => {
    return cats.map(category => (
      <React.Fragment key={category.id}>
        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0">
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
            <div className="flex items-center" style={{ paddingLeft: `${level * 20}px` }}>
              {category.subCategories && category.subCategories.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleCategoryExpansion(category.id)}
                  className="mr-2 focus:outline-none text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
                >
                  {expandedCategories.has(category.id) ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </button>
              )}
              {(!category.subCategories || category.subCategories.length === 0) && (
                <span className="mr-4 w-3"></span>
              )}
              <span className="text-gray-500 dark:text-gray-400 mr-2">{getFaIcon(category.icon || 'FaFolder', 16)}</span>
              <span>{category.name}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
            <button
              onClick={() => handleEditCategory(category.id)}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-4 transition-colors"
              title="Edit Category"
            >
              <FaEdit className="inline-block text-lg" />
            </button>
            <button
              onClick={() => handleDeleteClick(category)}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Delete Category"
            >
              <FaTrashAlt className="inline-block text-lg" />
            </button>
          </td>
        </tr>
        {expandedCategories.has(category.id) && category.subCategories && category.subCategories.length > 0 && (
          renderCategoryRows(category.subCategories, level + 1)
        )}
      </React.Fragment>
    ));
  };


  return (
    <AdminLayout>
      {/* Success Message Banner */}
      {message && (
        <div className="p-4 mb-6 rounded-lg text-white bg-green-600 shadow-lg flex items-center justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-white hover:text-gray-200"><FaIcons.FaTimes/></button>
        </div>
      )}

      {/* Header & Actions */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-4 transition-colors">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white hidden md:block">Category List</h1>
        
        <div className="flex w-full md:w-auto gap-4">
            <div className="relative w-full md:w-80">
            <input
                type="text"
                placeholder="Search categories..."
                className="w-full p-3 pl-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors
                bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500
                dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <button
            onClick={handleAddCategory}
            className="whitespace-nowrap bg-green-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-green-700 transition-colors flex items-center justify-center font-semibold"
            >
            <FaPlus className="mr-2" /> <span className="hidden sm:inline">Add Category</span>
            </button>
        </div>
      </div>

      {/* Category Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {filteredCategories.length > 0 ? (
                renderCategoryRows(filteredCategories)
                ) : (
                <tr>
                    <td colSpan={2} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                        No categories found matching your search.
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>

      {/* CONFIRM DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-2xl max-w-sm w-full border border-gray-200 dark:border-gray-700 transform transition-all">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Confirm Deletion</h3>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
                Are you sure you want to delete category: <span className="font-bold text-red-600 dark:text-red-400">{categoryToDelete?.name}</span>?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WARNING POP-UP MODAL */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700 text-center">
            <div className="flex justify-center mb-4 text-orange-500">
              <FaExclamationTriangle size={48} />
            </div>
            
            <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Action Denied</h3>
            
            <p className="mb-6 text-gray-600 dark:text-gray-300 text-sm whitespace-pre-line leading-relaxed">
              {warningMessage}
            </p>
            
            <button
              onClick={() => setShowWarningModal(false)}
              className="w-full px-6 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold transition-colors border border-gray-300 dark:border-gray-600"
            >
              Okay, I understand
            </button>
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default CategoryListPage;